import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import logoMark from "../assets/logo-mark.png";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Clients", href: "#testimonials" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-shadow duration-300 ${
        scrolled ? "bg-black/40 border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)]" : "bg-black/20 border-white/5"
      }`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img src={logoMark} alt="FCY IT Consultancy logo" className="h-8 w-8 object-contain" />
          <span className="font-display font-semibold tracking-tight text-lg text-foreground">
            FCY <span className="text-primary">IT</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Button
              variant="ghost"
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="h-11 px-3 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-white/5"
              data-testid={`navbar-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => scrollTo("#contact")}
            className="hidden sm:inline-flex rounded-xl h-10 px-5 font-medium"
            data-testid="navbar-contact-button"
          >
            Contact <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-foreground"
                aria-label="Open menu"
                data-testid="navbar-mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-card border-white/10 w-72" data-testid="navbar-mobile-menu">
              <SheetTitle className="sr-only" data-testid="navbar-mobile-menu-title">Explore FCY IT</SheetTitle>
              <SheetDescription className="sr-only" data-testid="navbar-mobile-menu-description">Navigate to our services, portfolio, clients and contact form.</SheetDescription>
              <div className="flex flex-col gap-2 mt-10">
                {NAV_LINKS.map((link) => (
                  <Button
                    variant="ghost"
                    key={link.href}
                    onClick={() => scrollTo(link.href)}
                    className="h-12 justify-start px-4 text-base text-foreground rounded-xl hover:bg-white/5 hover:text-foreground"
                    data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Button>
                ))}
                <Button
                  onClick={() => scrollTo("#contact")}
                  className="mt-4 rounded-xl h-12 font-medium"
                  data-testid="navbar-mobile-contact-button"
                >
                  Contact Us <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
};
